import { useState } from 'react';

interface Project {
  id: number;
  title: string;
  description: string;
  image: string;
  category: string;
  link: string;
  order: number;
}

interface PortfolioEditorProps {
  data: Project[];
  onChange: (data: Project[]) => void;
}

export default function PortfolioEditor({ data, onChange }: PortfolioEditorProps) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Project | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const handleEdit = (project: Project) => {
    setEditingId(project.id);
    setEditForm({ ...project });
    setShowAddForm(false);
  };

  const handleSave = () => {
    if (!editForm) return;
    const updatedData = data.map((p) => (p.id === editForm.id ? editForm : p));
    onChange(updatedData);
    setEditingId(null);
    setEditForm(null);
  };

  const handleDelete = (id: number) => {
    if (confirm('¿Estás seguro de eliminar este proyecto?')) {
      onChange(data.filter((p) => p.id !== id));
    }
  };

  const handleAdd = () => {
    const newProject: Project = {
      id: Math.max(...data.map((p) => p.id), 0) + 1,
      title: '',
      description: '',
      image: '',
      category: '',
      link: '#',
      order: data.length + 1,
    };
    setEditForm(newProject);
    setShowAddForm(true);
    setEditingId(null);
  };

  const handleAddSave = () => {
    if (!editForm) return;
    onChange([...data, editForm]);
    setShowAddForm(false);
    setEditForm(null);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newData = [...data];
    [newData[index], newData[index - 1]] = [newData[index - 1], newData[index]];
    newData.forEach((item, idx) => {
      item.order = idx + 1;
    });
    onChange(newData);
  };

  const moveDown = (index: number) => {
    if (index === data.length - 1) return;
    const newData = [...data];
    [newData[index], newData[index + 1]] = [newData[index + 1], newData[index]];
    newData.forEach((item, idx) => {
      item.order = idx + 1;
    });
    onChange(newData);
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-briefcase-line text-blue-600"></i>
            Gestionar Portafolio
          </h2>
          <p className="text-gray-600 mt-1">Agregar, editar o eliminar proyectos</p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Agregar Proyecto
        </button>
      </div>

      {(showAddForm || editingId !== null) && editForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {showAddForm ? 'Nuevo Proyecto' : 'Editar Proyecto'}
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre del proyecto"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoría</label>
                <input
                  type="text"
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Web Design, Social Media, etc."
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Descripción breve del proyecto"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">URL de Imagen</label>
              <input
                type="text"
                value={editForm.image}
                onChange={(e) => setEditForm({ ...editForm, image: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://ejemplo.com/imagen.jpg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enlace (opcional)
              </label>
              <input
                type="text"
                value={editForm.link}
                onChange={(e) => setEditForm({ ...editForm, link: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://proyecto.com"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={showAddForm ? handleAddSave : handleSave}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-save-line mr-2"></i>
                Guardar
              </button>
              <button
                onClick={() => {
                  setEditingId(null);
                  setEditForm(null);
                  setShowAddForm(false);
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.map((project, index) => (
          <div
            key={project.id}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            {project.image && (
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-48 object-cover"
              />
            )}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{project.title}</h3>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {project.category}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="p-1 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
                    title="Mover arriba"
                  >
                    <i className="ri-arrow-up-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => moveDown(index)}
                    disabled={index === data.length - 1}
                    className="p-1 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
                    title="Mover abajo"
                  >
                    <i className="ri-arrow-down-line text-sm"></i>
                  </button>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-3">{project.description}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(project)}
                  className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-100 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-edit-line mr-1"></i>
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(project.id)}
                  className="flex-1 bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-1"></i>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
